import React, { useState } from 'react';
import { Input, Button, Typography, Space, Tag, Badge, Tooltip } from 'antd';
import { FolderOutlined, FolderOpenOutlined, TagOutlined, PlusOutlined, DeleteOutlined, DownOutlined, UpOutlined, SearchOutlined, EditOutlined, StarOutlined, StarFilled, RightOutlined } from '@ant-design/icons';

const { Title } = Typography;

// 侧边栏组件，用于展示文件夹和标签
function Sidebar({ 
  folders = [], 
  tags = [], 
  selectedFolderId, 
  selectedTagIds = [], 
  onFolderSelect, 
  onTagSelect, 
  onAddFolder, 
  onAddTag, 
  onEditFolder,
  newFolderName, 
  setNewFolderName, 
  onSaveFolder, 
  showAddFolder, 
  setShowAddFolder, 
  newTagName, 
  setNewTagName, 
  onSaveTag, 
  showAddTag, 
  setShowAddTag, 
  onDeleteFolder, 
  onDeleteTag,
  folderCounts = {},
  tagCounts = {},
  onClearAllTagsAndFolders,
  onCloudBackup,
  favoritePrompts = [],
  onFavoriteSelect,
  viewingFavorites,
  onClearFavoriteSelection,
  selectedFavorite,
  onResetDatabase,
  onClearSelection
}) {
  // 文件夹编辑状态
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editFolderName, setEditFolderName] = useState('');

  // 处理文件夹添加表单提交
  const handleFolderSubmit = (e) => {
    e.preventDefault();
    onSaveFolder();
  };

  // 处理标签添加表单提交
  const handleTagSubmit = (e) => {
    e.preventDefault();
    onSaveTag();
  };

  // 开始编辑文件夹
  const startEditFolder = (e, folder) => {
    e.stopPropagation();
    setEditingFolderId(folder.id);
    setEditFolderName(folder.name);
  };

  // 保存编辑后的文件夹名称
  const saveEditedFolder = (e) => {
    e.preventDefault();
    if (editFolderName.trim() && editingFolderId) {
      onEditFolder(editingFolderId, editFolderName.trim());
      cancelEditFolder();
    }
  };

  // 取消编辑文件夹
  const cancelEditFolder = () => {
    setEditingFolderId(null);
    setEditFolderName('');
  };

  // 标签展开/折叠状态
  const [showAllTags, setShowAllTags] = useState(false);
  // 收藏展开/折叠状态
  const [showAllFavorites, setShowAllFavorites] = useState(false);
  // 收藏夹部分的折叠/展开状态
  const [collapseFavorites, setCollapseFavorites] = useState(false);
  // 标签搜索
  const [tagSearchText, setTagSearchText] = useState('');
  
  // 初始显示的标签数量
  const initialTagCount = 3;
  
  // 根据搜索文本过滤标签
  const filteredTags = tags.filter(tag => 
    tag.name.toLowerCase().includes(tagSearchText.toLowerCase())
  );
  
  // 按使用频率排序标签（使用tagCounts）
  const sortedTags = [...filteredTags].sort((a, b) => {
    const countA = tagCounts[a.id] || 0;
    const countB = tagCounts[b.id] || 0;
    return countB - countA; // 降序排列，使用频率高的排在前面
  });
  
  // 决定显示哪些标签
  const visibleTags = showAllTags ? sortedTags : sortedTags.slice(0, initialTagCount);
  
  // 是否有更多标签可以显示
  const hasMoreTags = sortedTags.length > initialTagCount;

  return (
    <div className="sidebar glassmorphism scrollbar-elegant p-6 w-64 h-full overflow-y-auto">
      {/* 文件夹部分 */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <Title level={5} className="text-[var(--apple-text-primary)] m-0 font-medium">文件夹</Title>
          <Tooltip title="添加文件夹">
            <Button 
              type="text" 
              size="small" 
              icon={<PlusOutlined />} 
              onClick={() => setShowAddFolder(true)}
              className="hover:bg-[var(--apple-hover-bg)] text-[var(--apple-text-secondary)] hover:text-[var(--apple-accent)]"
              shape="circle"
            />
          </Tooltip>
        </div>

        {/* 添加文件夹表单 */}
        {showAddFolder && (
          <form onSubmit={handleFolderSubmit} className="mb-4">
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <Input
                placeholder="输入文件夹名称..."
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="input-modern"
                autoFocus
              />
              <div className="flex space-x-2">
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  className="btn-modern-primary flex-1"
                  disabled={!newFolderName.trim()}
                >
                  保存
                </Button>
                <Button 
                  onClick={() => setShowAddFolder(false)}
                  className="btn-modern-secondary flex-1"
                >
                  取消
                </Button>
              </div>
            </Space>
          </form>
        )}

        {/* 文件夹列表 */}
        <div className="space-y-2">
          <div 
            className={`flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-all ${selectedFolderId === null ? 'bg-[var(--apple-selected-bg)] text-[var(--apple-accent)]' : 'hover:bg-[var(--apple-hover-bg)]'}`}
            onClick={() => onFolderSelect(null)}
          >
            <div className="flex items-center">
              <FolderOutlined className="mr-2" />
              <span className="font-medium">全部提示词</span>
            </div>
            <Badge 
              count={Object.values(folderCounts).reduce((a, b) => a + b, 0) || Object.keys(folderCounts).length === 0 ? folderCounts['all'] || 0 : 0} 
              className="badge-clean"
            />
          </div>

          {folders.map(folder => (
            <div
              key={folder.id}
              className={`group flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-all ${selectedFolderId === folder.id ? 'bg-[var(--apple-selected-bg)] text-[var(--apple-accent)]' : 'hover:bg-[var(--apple-hover-bg)]'}`}
              onClick={() => editingFolderId !== folder.id && onFolderSelect(folder.id)}
            >
              {editingFolderId === folder.id ? (
                // 编辑模式
                <form onSubmit={saveEditedFolder} className="w-full flex items-center">
                  <Input
                    value={editFolderName}
                    onChange={(e) => setEditFolderName(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="input-modern flex-grow mr-2"
                    autoFocus
                  />
                  <div className="flex">
                    <Button 
                      type="primary" 
                      htmlType="submit" 
                      size="small"
                      className="mr-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      保存
                    </Button>
                    <Button 
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        cancelEditFolder();
                      }}
                    >
                      取消
                    </Button>
                  </div>
                </form>
              ) : (
                // 查看模式
                <>
                  <div 
                    className="flex items-center flex-grow max-w-[85%] overflow-hidden"
                  >
                    {selectedFolderId === folder.id ? <FolderOpenOutlined className="mr-2 flex-shrink-0" /> : <FolderOutlined className="mr-2 flex-shrink-0" />}
                    <span className="truncate font-medium">{folder.name}</span>
                  </div>
                  
                  <div className="flex items-center ml-2">
                    <Badge 
                      count={folderCounts[folder.id] || 0} 
                      className="badge-clean mr-2"
                    />
                    <Tooltip title="编辑文件夹">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => startEditFolder(e, folder)}
                        className="opacity-70 hover:opacity-100 hover:text-[var(--apple-accent)] mr-1"
                        style={{ width: '24px', height: '24px', minWidth: '24px', padding: 0 }}
                      />
                    </Tooltip>
                    <Tooltip title="删除文件夹">
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteFolder(folder.id);
                        }}
                        className="opacity-70 hover:opacity-100 hover:text-[var(--apple-danger)] hover:bg-[rgba(255,77,79,0.1)]"
                        style={{ width: '24px', height: '24px', minWidth: '24px', padding: 0 }}
                      />
                    </Tooltip>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 分割线 */}
      <div className="divider-elegant my-6"></div>

      {/* 收藏夹部分 */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4 cursor-pointer" onClick={() => setCollapseFavorites(!collapseFavorites)}>
          <div className="flex items-center">
            {collapseFavorites ? 
              <RightOutlined className="mr-2 text-[var(--apple-text-secondary)]" /> : 
              <DownOutlined className="mr-2 text-[var(--apple-text-secondary)]" />
            }
            <Title level={5} className="text-[var(--apple-text-primary)] m-0 font-medium">收藏夹</Title>
            <Badge 
              count={favoritePrompts.length} 
              className="badge-clean ml-2"
              style={{ display: collapseFavorites ? 'inline-block' : 'none' }}
            />
          </div>
          {/* 移除了展开/折叠按钮 */}
        </div>

        {!collapseFavorites && (
          <div className="space-y-2">
            <div 
              className={`flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-all ${viewingFavorites && !selectedFavorite ? 'bg-[var(--apple-selected-bg)] text-[var(--apple-accent)]' : 'hover:bg-[var(--apple-hover-bg)]'}`}
              onClick={() => {
                if (viewingFavorites && !selectedFavorite) {
                  onClearFavoriteSelection();
                } else {
                  onClearSelection();
                  onFavoriteSelect(null);
                }
              }}
            >
              <div className="flex items-center">
                <StarFilled className="mr-2 text-amber-400" />
                <span className="font-medium">所有收藏</span>
              </div>
              <Badge 
                count={favoritePrompts.length} 
                className="badge-clean"
              />
            </div>

            {favoritePrompts.length > 0 ? (
              // 只显示前1个收藏，或者根据showAllFavorites状态显示全部
              (showAllFavorites ? favoritePrompts : favoritePrompts.slice(0, 1)).map((prompt, index) => (
                <div
                  key={prompt.id}
                  className={`flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-all ${viewingFavorites && selectedFavorite === prompt.id ? 'bg-[var(--apple-selected-bg)] text-[var(--apple-accent)]' : 'hover:bg-[var(--apple-hover-bg)]'}`}
                  onClick={() => onFavoriteSelect(prompt.id)}
                >
                  <div className="flex items-center flex-grow max-w-[85%] overflow-hidden">
                    <StarOutlined className="mr-2 text-amber-400 opacity-70" />
                    <span className="truncate font-medium">{prompt.title}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-[var(--apple-text-secondary)] text-sm py-2 w-full">
                暂无收藏，点击提示词卡片右上角的星标图标添加收藏
              </div>
            )}
            
            {/* 显示更多收藏按钮 */}
            {favoritePrompts.length > 1 && !showAllFavorites && (
              <Button
                type="text"
                size="small"
                className="w-full text-[var(--apple-text-secondary)] hover:text-[var(--apple-accent)] hover:bg-[var(--apple-hover-bg)] rounded-xl py-1.5 flex items-center justify-center transition-all duration-300 group"
                onClick={() => setShowAllFavorites(true)}
              >
                <span className="font-medium">显示更多 ({favoritePrompts.length - 1})</span>
                <div className="ml-2 p-0.5 rounded-full bg-gray-100 dark:bg-gray-800 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/30 transition-all duration-300">
                  <DownOutlined className="text-[var(--apple-text-secondary)] group-hover:text-[var(--apple-accent)] transform group-hover:translate-y-0.5 transition-all duration-300" />
                </div>
              </Button>
            )}
          </div>
        )}
      </div>
    
      {/* 分割线 */}
      <div className="divider-elegant my-6"></div>

      {/* 标签部分 */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <Title level={5} className="text-[var(--apple-text-primary)] m-0 font-medium">标签</Title>
          <div className="flex items-center">
            <Tooltip title="搜索标签">
              <Button 
                type="text" 
                size="small" 
                icon={<SearchOutlined />} 
                onClick={() => document.getElementById('tagSearchInput').focus()}
                className="hover:bg-[var(--apple-hover-bg)] text-[var(--apple-text-secondary)] hover:text-[var(--apple-accent)] mr-1"
                shape="circle"
              />
            </Tooltip>
            <Tooltip title="添加标签">
              <Button 
                type="text" 
                size="small" 
                icon={<PlusOutlined />} 
                onClick={() => setShowAddTag(true)}
                className="hover:bg-[var(--apple-hover-bg)] text-[var(--apple-text-secondary)] hover:text-[var(--apple-accent)]"
                shape="circle"
              />
            </Tooltip>
          </div>
        </div>

        {/* 标签搜索框 */}
        <div className="mb-3 tag-search-input">
          <Input
            id="tagSearchInput"
            placeholder="搜索标签..."
            prefix={<SearchOutlined className="text-[var(--apple-text-secondary)]" />}
            value={tagSearchText}
            onChange={(e) => setTagSearchText(e.target.value)}
            className="input-modern"
            allowClear
          />
        </div>

        {/* 添加标签表单 */}
        {showAddTag && (
          <form onSubmit={handleTagSubmit} className="mb-4">
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <Input
                placeholder="输入标签名称..."
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                className="input-modern"
                autoFocus
              />
              <div className="flex space-x-2">
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  className="btn-modern-primary flex-1"
                  disabled={!newTagName.trim()}
                >
                  保存
                </Button>
                <Button 
                  onClick={() => setShowAddTag(false)}
                  className="btn-modern-secondary flex-1"
                >
                  取消
                </Button>
              </div>
            </Space>
          </form>
        )}

        {/* 标签列表 */}
        <div className="tags-container flex flex-wrap gap-2">
          {filteredTags.length === 0 ? (
            <div className="text-center text-[var(--apple-text-secondary)] text-sm py-2 w-full">
              {tagSearchText ? '没有找到匹配的标签' : '暂无标签，点击右上角"+"添加'}
            </div>
          ) : (
            <>
              {visibleTags.map(tag => {
                // 为每个标签分配一个颜色类
                const colorClasses = ['blue', 'green', 'purple', 'orange', 'teal', 'red'];
                const colorClass = colorClasses[Math.abs(tag.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % colorClasses.length];
                
                return (
                  <Tooltip key={tag.id} title={`${tagCounts[tag.id] || 0} 个提示词`} placement="right">
                    <Tag
                      className={`tag-modern ${colorClass} ${selectedTagIds.includes(tag.id) ? 'selected' : ''}`}
                      onClick={() => onTagSelect(tag.id)}
                      closable
                      onClose={(e) => {
                        e.stopPropagation();
                        onDeleteTag(tag.id);
                      }}
                    >
                      <TagOutlined className="mr-1" />
                      <span className="truncate max-w-[100px]">{tag.name}</span>
                      {tagCounts[tag.id] > 0 && (
                        <Badge 
                          count={tagCounts[tag.id]} 
                          size="small"
                          className="badge-clean"
                          style={{ 
                            marginLeft: '4px'
                          }}
                        />
                      )}
                    </Tag>
                  </Tooltip>
                );
              })}
              
              {/* 显示更多/收起按钮 */}
              {hasMoreTags && !tagSearchText && (
                <Button
                  type="text"
                  size="small"
                  className="tag-modern-toggle flex items-center justify-center"
                  onClick={() => setShowAllTags(!showAllTags)}
                  icon={showAllTags ? <UpOutlined /> : <DownOutlined />}
                >
                  {showAllTags ? '收起' : `显示更多 (${sortedTags.length - initialTagCount})`}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      {/* 底部管理按钮 */}
      <div className="mt-8 space-y-2">
        {/* 云端备份按钮已移至顶部导航栏 */}
      </div>
    </div>
  );
}

export default Sidebar;
